import React, { useEffect, useState } from 'react';
import styled from 'styled-components/macro';
import { CircularProgress } from '@material-ui/core/';
import { makeStyles } from '@material-ui/core/styles';
import Grid from '@material-ui/core/Grid';
import Tooltip from '@material-ui/core/Tooltip';
import HelpIcon from '@material-ui/icons/Help';
import { compose } from 'ramda';
import { RouteComponentProps } from 'react-router-dom';
import { showToast, BackTo, Typography, Button } from '@deeptrue-frontend/ui';
import { ProjectStatus } from 'consts/statuses';
import { withMetaDescription } from 'components/HeadProvider/HeadProvider';
import ProjectMenu from '../Common/ProjectMenu';
import { withProject, WithProjectProps } from '../withProject';
import { useFormatMessage } from 'components/IntlProvider';
import { Dashboard } from 'components/Layout';
import { OrganizationType } from 'store/modules/auth/auth';
import StoreProvider from 'components/StoreProvider/StoreProvider';
import api from 'api/api';
import PaymentsBarReminder from 'components/Project/Subscriptions/PaymentBarReminder';
import DataCollectionCompletedNotification from 'components/Project/Common/DataCollectionCompletedNotification';

const { connect, selectors } = StoreProvider;

const Container = styled.div`
  display: flex;
  flex-wrap: wrap;
  width: 100%;
`;

const Box = styled(Grid)`
  display: flex;
  flex-direction: column;
  margin-bottom: 2rem;

  @media (min-width: 960px) {
    &:first-child {
      padding-right: 1.5rem;
    }
    &:last-child {
      padding-left: 1.5rem;
    }
  }
`;

const Title = styled(Typography)`
  font-weight: 500;
`;

const HeaderContainer = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
`;

const Table = styled.div`
  background-color: ${({ theme }) => theme.colors.white};
  border-radius: 4px;
  overflow: hidden;
  border: 1px solid #e9e9e9;
`;
const TableRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1rem 2.5rem;
  border-bottom: 1px solid #e9e9e9;

  &:last-child {
    border-bottom: 0;
  }
`;

const HeaderTableRow = styled.div`
  background-color: #fafafa;
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1rem 2.5rem;
  border-bottom: 1px solid #e9e9e9;

  &:last-child {
    border-bottom: 0;
  }
`;

const BlueTableRow = styled.div`
  background-color: #e8effc;
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1rem 2.5rem;
  border-bottom: 1px solid #e9e9e9;

  &:last-child {
    border-bottom: 0;
  }
`;

const LoaderWrapper = styled.div`
  display: flex;
  justify-content: center;
  margin-top:5rem;
  align-items: center;
  width: 100%;
  height: 100%:
`;

const ProjectMenuWrapper = styled.div`
  width: calc(100% - 2rem);
  .tab-bar {
    margin-left: -2rem;
    padding-left: 2rem;
    margin-right: -2rem;
    padding-right: 2rem;
  }
`;

const Td = styled(Typography)<{ isBold?: boolean }>`
  ${({ isBold }) => isBold && `font-weight: 600`};
  display: flex;
  align-items: center;
`;

const StyledHelpIcon = styled(HelpIcon)`
  color: #273958;
  margin-right: 0.5rem;
`;

const StickyButton = styled(Button)`
  position: fixed;
  bottom: 2.875rem;
  right: 2rem;
  box-shadow: 0px 4px 4px rgba(0, 0, 0, 0.25);
`;

const SubtitleContainer = styled.div`
  height: 3.625rem;
`;

const useStylesBootstrap = makeStyles(() => ({
  arrow: {
    color: '#273958',
    left: '.5rem !important',
  },
  tooltip: {
    backgroundColor: '#273958',
    fontSize: '0.875rem',
    padding: '0.5rem 0.75rem',
    marginLeft: '-0.5rem',
  },
}));

const StyledTooltip = (props: any) => {
  const classes = useStylesBootstrap();

  return <Tooltip arrow classes={classes} {...props} />;
};

interface Props extends RouteComponentProps<{ id: string; codename: string }> {
  organization: OrganizationType;
  project: WithProjectProps['project'];
  isBexioUser: boolean;
}

const Statistics = ({
  location,
  history,
  match: { params },
  isBexioUser,
  organization,
  project,
}: Props) => {
  const [statistic, setStatistic] = useState({
    untouched: null,
    started: null,
    screenout: null,
    quotafull: null,
    completed: null,
    total: null,
    quantiles: {
      Q1: null,
      Q2: null,
      Q3: null,
    },
    quality_terminate: null,
  });
  const [isLoading, setIsLoading] = useState(true);
  const t = useFormatMessage();

  useEffect(() => {
    const orgName = params.codename || organization.codename;

    if (project.id !== -1) {
      api.resources
        .getSurveysStatistics({
          orgName: orgName,
          uuid: project.uuid,
        })
        .then(({ data }) => {
          setStatistic(data);
          setIsLoading(false);
        })
        .catch(() => {
          setIsLoading(false);
          showToast({
            type: 'error',
            title: t(
              'errors.toast.general',
              'Something went wrong, please contact administrator'
            ),
          });
        });
    }
    // eslint-disable-next-line
  }, [project]);

  const parseQuartille = (time: string | null) => {
    const onlySeconds = Number(Number(time).toFixed());
    if (onlySeconds < 60) {
      const secToDisplay = onlySeconds < 10 ? `0${onlySeconds}` : onlySeconds;
      return `0:${secToDisplay}`;
    }

    const minutes = Math.floor(onlySeconds / 60);
    const restOfSec = onlySeconds % 60;
    const secToDisplay = restOfSec < 10 ? `0${restOfSec}` : restOfSec;

    return `${minutes}:${secToDisplay}`;
  };
  const {
    untouched,
    started,
    screenout,
    quotafull,
    completed,
    total,
    quantiles,
    quality_terminate,
  } = statistic;

  const { id, status, subscription, is_subscribable } = project;

  const shouldDisplayCardReminder = is_subscribable ? !subscription : false;

  const overviewUrl = params.codename
    ? `/admin/organizations/${params.codename}/projects/`
    : `/`;

  const reportUrl = params.codename
    ? `/admin/organizations/${params.codename}/projects/${project.id}/reports/`
    : `/project/${project.id}/reports/`;
  const collectDataUrl = params.codename
    ? `/admin/organizations/${params.codename}/projects/${project.id}/collect-data/`
    : `/project/${project.id}/collect-data/`;

  useEffect(() => {
    if (!isLoading) {
      if (project.status.value === ProjectStatus.COLLECTING_DATA) {
        showToast({
          type: 'collectingData',
          title: t(
            'project.testEdit.toats.collecting',
            'Data collection started. You can still add new respondents to your project. '
          ),
          id: 'collection-started',
          cta: t('project.testEdit.toats.cta', 'Add respondents'),
          onCtaClick: () => history.push(collectDataUrl),
        });
      }
    }
  }, [project, isLoading, history, t, collectDataUrl]);

  return (
    <Dashboard
      title={project.name || t('page.project', 'Project details')}
      location={location}
      isFullWidth
      combinedTitle={project.combinedTitle}
    >
      {!isBexioUser && (
        <BackTo onClick={() => history.push(overviewUrl)}>
          {t('project.backTo.overview', 'Back to overview')}
        </BackTo>
      )}
      <ProjectMenuWrapper>
        <ProjectMenu
          id={id}
          projectStatus={status}
          isProjectSubscribable={is_subscribable}
          isProjectLoaded={project.id !== -1}
        />
      </ProjectMenuWrapper>
      {isLoading ? (
        <LoaderWrapper>
          <CircularProgress size={80} color="primary" />
        </LoaderWrapper>
      ) : (
        <>
          {shouldDisplayCardReminder && <PaymentsBarReminder />}
          <HeaderContainer>
            <Typography
              variant="h4"
              mt="2.31rem"
              fontSize="1.125rem"
              fontWeight="500"
              color="#353531"
            >
              {t('project.statistic.title', 'Progress')}
            </Typography>

            {project.status.value === ProjectStatus.FINISHED &&
            project.data_collection_method === 2 ? (
              <DataCollectionCompletedNotification
                isManualDataCollection={project.data_collection_method === 2}
                noData={completed === 0}
              />
            ) : (
              <Typography
                variant="h6"
                mt="0.5rem"
                fontSize="0.875rem"
                fontWeight="400"
                lineHeight="1.25rem"
                mb="1.5rem"
                color="#353531"
              >
                {t(
                  'project.statistic.subtitle',
                  'Here you can see the current participation progress of the survey.'
                )}
              </Typography>
            )}
          </HeaderContainer>
          <Container>
            <Box item xs={12} sm={12} md={6}>
              <Title variant="copy1" mb="0.5rem">
                {t('project.statistic.invitations.send', 'Invitations sent')}
              </Title>
              <SubtitleContainer>
                <Typography variant="copy2" mb="1rem">
                  {t(
                    'project.statistic.invitations.desc',
                    ' We are tracking all the invitations you sent here it is the performance'
                  )}
                </Typography>
              </SubtitleContainer>

              <Table>
                <HeaderTableRow>
                  <Td isBold>{t('project.statistic.status', 'Status')}</Td>
                  <Td isBold>{t('project.statistic.quantity', 'Quantity')}</Td>
                </HeaderTableRow>
                <TableRow>
                  <Td isBold>
                    {t('project.statistic.totalFinished', 'Total Finished')}
                  </Td>
                  <Td isBold>{total && total}</Td>
                </TableRow>
                <TableRow>
                  <Td>
                    <StyledTooltip
                      arrow
                      placement="top-start"
                      title={t(
                        'project.statistic.tooltip.untouched',
                        'Number of panelists who haven’t started a survey yet.'
                      )}
                    >
                      <StyledHelpIcon />
                    </StyledTooltip>
                    {t('project.statistic.untouched', 'Not started')}
                  </Td>
                  <Td>{untouched && untouched}</Td>
                </TableRow>
                <TableRow>
                  <Td>
                    <StyledTooltip
                      arrow
                      placement="top-start"
                      title={t(
                        'project.statistic.tooltip.started',
                        'Number of panelists who have started a survey, but not yet answered all questions.'
                      )}
                    >
                      <StyledHelpIcon />
                    </StyledTooltip>
                    {t('project.statistic.started', 'Started')}
                  </Td>
                  <Td>{started && started}</Td>
                </TableRow>
                <TableRow>
                  <Td>
                    <StyledTooltip
                      arrow
                      placement="top-start"
                      title={t(
                        'project.statistic.tooltip.screenout',
                        'Number of contacts who did not meet the criteria for participation in the survey.'
                      )}
                    >
                      <StyledHelpIcon />
                    </StyledTooltip>
                    {t('project.statistic.screenout', 'Screenout')}
                  </Td>
                  <Td>{screenout && screenout}</Td>
                </TableRow>
                <TableRow>
                  <Td>
                    <StyledTooltip
                      arrow
                      placement="top-start"
                      title={t(
                        'project.statistic.tooltip.quotafull',
                        'Number of panelists who were not allowed to complete the survey because sufficient responses have been collected for a specific age group.'
                      )}
                    >
                      <StyledHelpIcon />
                    </StyledTooltip>
                    {t('project.statistic.quotafull', 'Quota full')}
                  </Td>
                  <Td>{quotafull && quotafull}</Td>
                </TableRow>
                <TableRow>
                  <Td>
                    <StyledTooltip
                      arrow
                      placement="top-start"
                      title={t(
                        'project.statistic.tooltip.qualityTerminate',
                        'Number of contacts terminated on the basis of their conspicuous response behavior (speeder, pattern anchor, straightliner)..'
                      )}
                    >
                      <StyledHelpIcon />
                    </StyledTooltip>
                    {t(
                      'project.statistic.qualityTerminate',
                      'Quality Terminate'
                    )}
                  </Td>
                  <Td>{quality_terminate && quality_terminate}</Td>
                </TableRow>
                <BlueTableRow>
                  <Td>
                    <StyledTooltip
                      arrow
                      placement="top-start"
                      title={t(
                        'project.statistic.tooltip.complete',
                        'Number of panelists who have answered all questions of the survey.'
                      )}
                    >
                      <StyledHelpIcon />
                    </StyledTooltip>
                    {t('project.statistic.complete', 'Completed')}
                  </Td>
                  <Td>{completed && completed}</Td>
                </BlueTableRow>
              </Table>
            </Box>
            <Box item xs={12} sm={12} md={6}>
              <Title variant="copy1" mb="0.5rem">
                {t('project.statistic.duration', 'Duration')}
              </Title>
              <SubtitleContainer>
                <Typography variant="copy2" mb="1rem">
                  {t(
                    'project.statistic.duration.desc',
                    'Here you can see how long it took the respondents on average to complete the survey.'
                  )}
                </Typography>
              </SubtitleContainer>
              <Table>
                <HeaderTableRow>
                  <Td isBold>
                    {t('project.statistic.quartille.median', 'Median')}
                  </Td>
                </HeaderTableRow>
                <TableRow>
                  <Td>
                    <StyledTooltip
                      arrow
                      placement="top-start"
                      title={t(
                        'project.statistic.tooltip.duration',
                        'Average completion time in minutes and seconds'
                      )}
                    >
                      <StyledHelpIcon />
                    </StyledTooltip>
                    {parseQuartille(quantiles.Q2)}m
                  </Td>
                </TableRow>
              </Table>
            </Box>
            <StickyButton onClick={() => history.push(reportUrl)}>
              {t('project.viewReport', 'View Report')}
            </StickyButton>
          </Container>
        </>
      )}
    </Dashboard>
  );
};

export default compose(
  connect((state) => ({
    organization: selectors.authSelectors.userOrganization(state),
    reportsTables: selectors.reports.tables(state),
    isBexioUser: selectors.authSelectors.isBexioUser(state),
  })),
  withMetaDescription({ title: 'Dashboard projects' }),
  withProject(({ match, organization }: Props) => ({
    projectId: match.params.id,
    organizationCodename: match.params.codename || organization.codename,
  }))
)(Statistics);
